/*
 * The HTTP client.
 *
 * One function, because every call in this application is the same shape: a
 * cookie-authenticated JSON request against a same-origin path, whose failure
 * body is `{"error": "a sentence written for a person"}`.
 *
 * That sentence is thrown verbatim. The server writes its errors for the
 * reader — "Insufficient funds", "That code has expired", "This is the only
 * active admin" — and paraphrasing them in the browser is how a precise answer
 * becomes a vague one.
 */

export class ApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }

  /** A signed-out session, which the app handles by showing the sign-in page. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }
}

type Method = "GET" | "POST" | "PUT" | "DELETE";

async function request<T>(method: Method, path: string, body?: unknown): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, {
      method,
      credentials: "same-origin",
      headers: body === undefined ? {} : { "Content-Type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    // A dead network, not a refused request. Said as such — "failed to fetch"
    // is not an answer anybody can act on.
    throw new ApiError("Could not reach the server. Check your connection.", 0);
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  let payload: unknown = null;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = null;
    }
  }

  if (!response.ok) {
    const message =
      (payload && typeof payload === "object" && "error" in payload
        ? String((payload as { error: unknown }).error)
        : "") || "Something went wrong. Please try again.";
    throw new ApiError(message, response.status);
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body),
  put: <T>(path: string, body?: unknown) => request<T>("PUT", path, body),
  del: <T>(path: string, body?: unknown) => request<T>("DELETE", path, body),
};

/** Where the price socket lives, on whichever scheme the page was served over. */
export function streamUrl(): string {
  const scheme = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${scheme}//${window.location.host}/api/market/stream`;
}
